import {
  CalendarCheck,
  Check,
  Cube,
  HandHeart,
  Link as LinkIcon,
  Lock,
  PaperPlaneTilt,
  PlusCircle,
  ShieldCheck,
  UsersThree,
} from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { usePageMeta } from "@/hooks/usePageMeta";
import { avatarColor, initials } from "@/lib/avatar";
import styles from "./HowItWorks.module.css";
import contentStyles from "./ContentPage.module.css";

const LIMIT_ROWS = [
  { key: "Participants" },
  { key: "Items" },
  { key: "Trash" },
  { key: "Stats" },
  { key: "Notifications" },
  { key: "Whatsapp" },
] as const;

const STACK_POINTS = [
  "stackFrontend",
  "stackBackend",
  "stackDb",
  "stackHosting",
  "stackMail",
  "stackPayment",
] as const;

const SECURITY_POINTS = [
  "securityKeys",
  "securityAltcha",
  "securityRetention",
  "securityCsp",
  "securityHsts",
] as const;

const COMMITMENTS = [
  "commitmentsNoAds",
  "commitmentsNoSale",
  "commitmentsNoUs",
  "commitmentsNoTracking",
  "commitmentsNoProfile",
] as const;

export function HowItWorks() {
  const { t } = useTranslation();
  usePageMeta({
    title: t("pageMeta.howItWorksTitle"),
    description: t("pageMeta.howItWorksDescription"),
  });
  return (
    <article className={contentStyles.page}>
      <header>
        <h1>{t("howItWorks.title")}</h1>
        <p className={contentStyles.lead}>{t("howItWorks.lead")}</p>
      </header>

      <section className={styles.flowSection}>
        <h2>{t("howItWorks.flowTitle")}</h2>
        <ol className={styles.flowSteps}>
          <FlowStep
            number={1}
            icon={<PlusCircle size={26} weight="regular" />}
            title={t("howItWorks.flowAdminTitle")}
            body={t("howItWorks.flowAdminBody")}
            tone="blue"
          >
            <CreateMockup />
          </FlowStep>
          <FlowStep
            number={2}
            icon={<PaperPlaneTilt size={26} weight="regular" />}
            title={t("howItWorks.flowGuestTitle")}
            body={t("howItWorks.flowGuestBody")}
            tone="gold"
          >
            <ShareMockup />
          </FlowStep>
          <FlowStep
            number={3}
            icon={<CalendarCheck size={26} weight="regular" />}
            title={t("howItWorks.flowResultTitle")}
            body={t("howItWorks.flowResultBody")}
            tone="green"
          >
            <ResultMockup />
          </FlowStep>
        </ol>
      </section>

      <section>
        <h2>{t("howItWorks.limitsTitle")}</h2>
        <p>{t("howItWorks.limitsLead")}</p>
        <div className={styles.tableWrap}>
          <table className={styles.limitsTable}>
            <thead>
              <tr>
                <th>{t("howItWorks.limitsFeature")}</th>
                <th>{t("howItWorks.limitsFree")}</th>
                <th>{t("howItWorks.limitsPro")}</th>
              </tr>
            </thead>
            <tbody>
              {LIMIT_ROWS.map(({ key }) => (
                <tr key={key}>
                  <th scope="row">{t(`howItWorks.row${key}`)}</th>
                  <td>{t(`howItWorks.row${key}Free`)}</td>
                  <td className={styles.proCell}>{t(`howItWorks.row${key}Pro`)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={styles.note}>{t("howItWorks.limitsNote")}</p>
      </section>

      <aside className={styles.manifest}>
        <div className={styles.manifestHead}>
          <span className={styles.manifestIcon}>
            <HandHeart size={22} weight="regular" />
          </span>
          <h2>{t("howItWorks.manifestTitle")}</h2>
        </div>
        <ol className={styles.manifestList}>
          <li>{t("howItWorks.manifest1")}</li>
          <li>{t("howItWorks.manifest2")}</li>
          <li>{t("howItWorks.manifest3")}</li>
          <li>{t("howItWorks.manifest4")}</li>
          <li>{t("howItWorks.manifest5")}</li>
        </ol>
        <p className={styles.manifestFooter}>{t("howItWorks.manifestSignoff")}</p>
      </aside>

      <section>
        <h2>{t("howItWorks.fundingTitle")}</h2>
        <p>{t("howItWorks.fundingBody")}</p>
      </section>

      <section>
        <h2>{t("howItWorks.transparencyTitle")}</h2>
        <p className={styles.sectionIntro}>{t("howItWorks.transparencyIntro")}</p>

        <div className={styles.infoBlock}>
          <h3>
            <Cube size={18} weight="regular" />
            {t("howItWorks.stackTitle")}
          </h3>
          <ul className={styles.checkList}>
            {STACK_POINTS.map((k) => (
              <li key={k}>
                <Check size={14} weight="bold" className={styles.checkIcon} />
                <span>{t(`howItWorks.${k}`)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.infoBlock}>
          <h3>
            <ShieldCheck size={18} weight="regular" />
            {t("howItWorks.securityTitle")}
          </h3>
          <ul className={styles.checkList}>
            {SECURITY_POINTS.map((k) => (
              <li key={k}>
                <Check size={14} weight="bold" className={styles.checkIcon} />
                <span>{t(`howItWorks.${k}`)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.infoBlock}>
          <h3>
            <Lock size={18} weight="regular" />
            {t("howItWorks.commitmentsTitle")}
          </h3>
          <ul className={styles.checkList}>
            {COMMITMENTS.map((k) => (
              <li key={k}>
                <Check size={14} weight="bold" className={styles.checkIcon} />
                <span>{t(`howItWorks.${k}`)}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </article>
  );
}

type StepTone = "blue" | "gold" | "green";

function FlowStep({
  number,
  icon,
  title,
  body,
  tone,
  children,
}: {
  number: number;
  icon: ReactNode;
  title: string;
  body: string;
  tone: StepTone;
  children: ReactNode;
}) {
  return (
    <li className={`${styles.step} ${styles[`stepTone_${tone}`]}`}>
      <div className={styles.stepHeader}>
        <span className={styles.stepBadge}>{number}</span>
        <span className={styles.stepIcon}>{icon}</span>
      </div>
      <h3 className={styles.stepTitle}>{title}</h3>
      <p className={styles.stepBody}>{body}</p>
      <div className={styles.mockup}>{children}</div>
    </li>
  );
}

function CreateMockup() {
  const { t } = useTranslation();
  return (
    <div className={styles.mockupCard} aria-hidden>
      <div className={styles.mockupField}>
        <span className={styles.mockupLabel}>
          {t("howItWorks.mockupCreateNameLabel")}
        </span>
        <span className={styles.mockupInput}>
          {t("howItWorks.mockupCreateName")}
        </span>
      </div>
      <div className={styles.mockupRow}>
        <div className={styles.mockupField}>
          <span className={styles.mockupLabel}>
            {t("howItWorks.mockupCreateDateLabel")}
          </span>
          <span className={styles.mockupInput}>
            {t("howItWorks.mockupCreateDate")}
          </span>
        </div>
        <div className={styles.mockupField}>
          <span className={styles.mockupLabel}>
            {t("howItWorks.mockupCreateTimeLabel")}
          </span>
          <span className={styles.mockupInput}>18:00</span>
        </div>
      </div>
      <span className={styles.mockupChip}>
        <PlusCircle size={12} weight="regular" />
        {t("howItWorks.mockupCreateCta")}
      </span>
    </div>
  );
}

function ShareMockup() {
  const { t } = useTranslation();
  const origin =
    typeof window !== "undefined" ? window.location.host : "brings-mit.com";
  return (
    <div className={styles.mockupBubble} aria-hidden>
      <span className={styles.mockupBubbleText}>
        {t("howItWorks.mockupShareMessage")}
      </span>
      <span className={styles.mockupBubbleLink}>
        <LinkIcon size={12} weight="regular" />
        {origin}/view/p2Xy…
      </span>
      <span className={styles.mockupBubbleMeta}>
        <UsersThree size={12} weight="regular" />
        {t("howItWorks.mockupShareMeta")}
      </span>
    </div>
  );
}

function ResultMockup() {
  const { t } = useTranslation();
  const lines: Array<{ name: string; item: string; state: "done" | "open" }> = [
    { name: t("howItWorks.mockupPersonA"), item: t("howItWorks.mockupItemA"), state: "done" },
    { name: t("howItWorks.mockupPersonB"), item: t("howItWorks.mockupItemB"), state: "done" },
    { name: "", item: t("howItWorks.mockupItemC"), state: "open" },
  ];
  return (
    <div className={styles.mockupList} aria-hidden>
      {lines.map((line, i) => (
        <div
          key={i}
          className={`${styles.mockupListRow} ${
            line.state === "open" ? styles.mockupListRowOpen : ""
          }`}
        >
          {line.name ? (
            <span
              className={styles.mockupAvatar}
              style={{ backgroundColor: avatarColor(line.name) }}
            >
              {initials(line.name)}
            </span>
          ) : (
            <span className={styles.mockupAvatarEmpty} />
          )}
          <span className={styles.mockupItemName}>{line.item}</span>
          <span className={styles.mockupItemTag}>
            {line.state === "done"
              ? t("howItWorks.mockupItemDone", { name: line.name })
              : t("howItWorks.mockupItemOpen")}
          </span>
        </div>
      ))}
    </div>
  );
}
